// src/contexts/UserContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useMsal, useAccount } from "@azure/msal-react";
import { graphConfig, loginRequest } from "../configs/auth/authConfig";
import CommonServices from "../services/commonServices";
import { clearLastSelectedCountryID, syncLastSelectedCountryFromUser } from "../utils/lastSelectedCountryStore";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [user, setUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [activeApp, setActiveApp] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState({ id: "", name: "" });
  const [activePage, setActivePage] = useState(localStorage.getItem("activePage"));
  const navigate = useNavigate();
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  useEffect(() => {
    getUserData();
  }, [account]);

  const getUserData = async () => {
    if (!account) return;

    try {
      setIsLoadingUser(true);
      const userData = account;
      if (userData) {
        const ok = await retrieveCurrentUserData(userData);
        if (ok) {
          await getApplications();
        }
      }

    } catch (err) {
      console.error("Error getting token/user:", err);
    }
    finally {
      // defer to next tick to allow state flush
      setTimeout(() => setIsLoadingUser(false), 0);
    }
  };

  const retrieveCurrentUserData = async (userProfile) => {
    try {
      const respData = await CommonServices.getCurrentUser(userProfile.localAccountId);
      if (respData?.success) {        
        const userData = respData?.responseObject?.value?.userDetails;
        if (userData) {
          setUser(userData);
          syncLastSelectedCountryFromUser(userData);
          setActiveApp(userData.lastSelectedApplicationID);
          setSelectedApp({ id: userData.lastSelectedApplicationID, name: userData.lastSelectedApplicationName ?? "-" });
          setIsUnauthorized(false);
          console.log("userData on context", userData);
          return true;
        }
        // success but no user details -> treat as unauthorized
        setUser(null);
        clearLastSelectedCountryID();
        setIsUnauthorized(true);
        navigate("/unauthorized", { replace: true });
        return false;
      }
      // Non-success: only unauthorized on 401
      if (respData?.status === 401 || respData?.responseObject?.status === 401) {
        setUser(null);
        clearLastSelectedCountryID();
        setIsUnauthorized(true);
        navigate("/unauthorized", { replace: true });
        return false;
      }
      // Other errors: do not mark unauthorized; allow retry/continue
      setIsUnauthorized(false);
      return false;

    } catch (error) {
      console.error('Failed to fetch:', error?.message);
      if (error?.response?.status === 401) {
        setUser(null);
        clearLastSelectedCountryID();
        setIsUnauthorized(true);
        navigate("/unauthorized", { replace: true });
      } else {
        setIsUnauthorized(false);
      }
      return false;
    }
  };

  const getApplications = async () => {
    try {
      const respData = await CommonServices.getApplications();
      if (respData.success) {
        const applications = respData.responseObject.value.applications;
        if (applications) {
          const objectsArray = applications.map((item) => ({ value: item.id, label: item.displayName, code: item.code }));
          setApplications(objectsArray);
        }
      }
    } catch (error) {
      console.error('Failed to fetch:', error.message);
    }
  };

  return (
    <UserContext.Provider value={{ user, isLoadingUser, isUnauthorized, activeApp, applications, selectedApp, setActiveApp, activePage, setActivePage, refreshUser: getUserData }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for using the context
export const useUserAuth = () => useContext(UserContext);
